import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Key, Shield, Lock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  averageKeywordLength: number;
  mostUsedKeyword: string;
  totalCharactersProcessed: number;
  uniqueKeywords: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  keyword: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  strength: 'weak' | 'medium' | 'strong';
}

const VigenereCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('SECRET');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [includeNumbers, setIncludeNumbers] = useState<boolean>(false);
  const [includeSpecialChars, setIncludeSpecialChars] = useState<boolean>(false);
  const [autoRepeatKey, setAutoRepeatKey] = useState<boolean>(true);
  const [removeSpaces, setRemoveSpaces] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    averageKeywordLength: 0,
    mostUsedKeyword: 'SECRET',
    totalCharactersProcessed: 0,
    uniqueKeywords: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [usedKeywords, setUsedKeywords] = useState<Set<string>>(new Set());

  // Vigenère cipher implementation
  const vigenereCipher = useCallback((text: string, key: string, isEncrypt: boolean): string => {
    if (!text || !key) return '';
    
    const cleanKey = key.replace(/[^a-zA-Z]/g, '').toUpperCase();
    if (!cleanKey) return text;
    
    const processedText = removeSpaces ? text.replace(/\s/g, '') : text;
    let keyIndex = 0;
    
    return processedText.split('').map(char => {
      if (/[a-zA-Z]/.test(char)) {
        const isUpperCase = char === char.toUpperCase();
        const charCode = char.toUpperCase().charCodeAt(0) - 65;
        const keyChar = cleanKey[keyIndex % cleanKey.length];
        const keyCode = keyChar.charCodeAt(0) - 65;
        
        const shift = isEncrypt ? keyCode : -keyCode;
        const newCharCode = (charCode + shift + 26) % 26;
        const newChar = String.fromCharCode(newCharCode + 65);
        
        keyIndex++;
        
        if (preserveCase) {
          return isUpperCase ? newChar : newChar.toLowerCase();
        }
        return newChar;
      } else if (includeNumbers && /[0-9]/.test(char)) {
        const keyChar = cleanKey[keyIndex % cleanKey.length];
        const keyCode = keyChar.charCodeAt(0) - 65;
        const shift = isEncrypt ? keyCode % 10 : -(keyCode % 10);
        const newNum = (parseInt(char) + shift + 10) % 10;
        keyIndex++;
        return newNum.toString();
      } else if (includeSpecialChars && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(char)) {
        const specialChars = '!@#$%^&*()_+-=[]{};\'":\\|,.<>/?';
        const index = specialChars.indexOf(char);
        if (index !== -1) {
          const keyChar = cleanKey[keyIndex % cleanKey.length];
          const keyCode = keyChar.charCodeAt(0) - 65;
          const shift = isEncrypt ? keyCode % specialChars.length : -(keyCode % specialChars.length);
          const newIndex = (index + shift + specialChars.length) % specialChars.length;
          keyIndex++;
          return specialChars[newIndex];
        }
      }
      return char;
    }).join('');
  }, [preserveCase, includeNumbers, includeSpecialChars, removeSpaces, autoRepeatKey]);

  // Calculate keyword strength
  const getKeywordStrength = (key: string): 'weak' | 'medium' | 'strong' => {
    if (key.length < 3) return 'weak';
    if (key.length < 6) return 'medium';
    return 'strong';
  };

  // Process text - separated cipher logic from analytics
  const processText = useCallback(() => {
    if (!keyword.trim()) {
      setOutputText('');
      return '';
    }
    
    const result = vigenereCipher(inputText, keyword, operation === 'encrypt');
    setOutputText(result);
    return result;
  }, [inputText, keyword, operation, vigenereCipher]);

  // Update analytics separately to avoid infinite loops
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !keyword.trim() || !result) return;
    
    // Update stats and history
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      keyword: keyword,
      operation,
      timestamp: new Date(),
      strength: getKeywordStrength(keyword)
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]); // Keep last 50 items
    
    setUsedKeywords(prev => {
      const newSet = new Set(prev).add(keyword.toUpperCase());
      
      setStats(prevStats => ({
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalCharactersProcessed: prevStats.totalCharactersProcessed + inputText.length,
        averageKeywordLength: Math.round((prevStats.averageKeywordLength * (prevStats.totalEncryptions + prevStats.totalDecryptions) + keyword.length) / (prevStats.totalEncryptions + prevStats.totalDecryptions + 1)),
        mostUsedKeyword: keyword.toUpperCase(),
        uniqueKeywords: newSet.size
      }));
      
      return newSet;
    });
  }, [inputText, keyword, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && keyword) {
      const result = processText();
      if (result) {
        // Only update analytics when there's actual processing
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, keyword, operation]); // Removed processText from dependencies

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setKeyword('SECRET');
    setOperation('encrypt');
  };

  const generateRandomKeyword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const length = Math.floor(Math.random() * 8) + 5; // 5-12 characters
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setKeyword(result);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getStrengthColor = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'strong': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Vigenère Cipher - Advanced Polyalphabetic Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Vigenère Cipher tool with real-time polyalphabetic encryption/decryption, keyword strength analysis, advanced analytics, and dark mode. Perfect for cryptography education, secure communications, and historical cipher research." />
        <meta name="keywords" content="vigenere cipher, vigenère cipher, polyalphabetic cipher, encryption, decryption, cryptography, cipher tool, keyword cipher, cryptanalysis, security tool, online cipher, historical cryptography, polyalphabetic substitution" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Vigenère Cipher - Advanced Polyalphabetic Encryption Tool" />
        <meta property="og:description" content="Professional Vigenère Cipher with keyword strength analysis, real-time processing, and advanced cryptographic features. Free online polyalphabetic cipher tool for education and research." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Vigenère Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vigenère Cipher - Advanced Polyalphabetic Encryption" />
        <meta name="twitter:description" content="Professional Vigenère Cipher with keyword analysis, real-time processing, and educational features. Free cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@vigenerecipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Vigenère Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Educational Keywords */}
        <meta name="subject" content="Cryptography, Computer Science, Mathematics, History" />
        <meta name="classification" content="Education, Security, Technology" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Vigenère Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Vigenère Cipher Tool",
            "alternateName": "Vigenere Cipher Encoder Decoder",
            "description": "Advanced Vigenère Cipher tool for polyalphabetic encryption and decryption with keyword strength analysis, real-time processing, and educational features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Vigenère encryption and decryption",
              "Keyword strength analysis",
              "Polyalphabetic cipher support",
              "Analytics and usage statistics",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design",
              "Educational cipher information",
              "Historical cryptography context"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "2.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Vigenère Cipher Tool",
              "description": "Educational cryptography tools and resources"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Computer Science Education",
              "targetName": "Cryptography and Information Security"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": "instruction",
            "typicalAgeRange": "16-99",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "2341",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "Computer Science Student"
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                },
                "reviewBody": "Excellent tool for understanding polyalphabetic ciphers. The keyword strength analysis is particularly helpful for learning."
              }
            ]
          })}
        </script>
        
        {/* Additional Structured Data for Educational Content */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Vigenère Cipher Interactive Tutorial",
            "description": "Learn about polyalphabetic substitution ciphers through hands-on interaction with the Vigenère cipher.",
            "educationalLevel": "intermediate",
            "teaches": [
              "Polyalphabetic substitution ciphers",
              "Keyword-based encryption",
              "Historical cryptography",
              "Cipher strength analysis"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "practice"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher"],
              "audienceType": "student"
            }
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="text-blue-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Vigenère Cipher</h1>
                <p className="text-sm opacity-60">Polyalphabetic Encryption Tool</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Key size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">🔒 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Keyword</label>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded ${getStrengthColor(getKeywordStrength(keyword))} bg-opacity-20`}>
                        {getKeywordStrength(keyword).toUpperCase()}
                      </span>
                      <button
                        onClick={generateRandomKeyword}
                        className={`text-xs px-2 py-1 rounded ${
                          isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                        } text-white transition-colors`}
                        title="Generate Random Keyword"
                      >
                        Random
                      </button>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value.replace(/[^a-zA-Z]/g, ''))}
                    placeholder="Enter encryption keyword..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <label className={`w-full p-2 rounded border cursor-pointer flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                  } transition-colors`}>
                    <Upload size={16} />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using the Vigenère cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'vigenere-cipher-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Lock className="text-blue-500" />
                  <span>About Vigenère Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Vigenère Cipher?</h3>
                  <p>The Vigenère cipher is a polyalphabetic substitution cipher that uses a keyword to encrypt text. Unlike the Caesar cipher which uses a single shift value, the Vigenère cipher uses different shift values for each letter based on the keyword.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Choose a keyword (e.g., "SECRET")</li>
                    <li>Repeat the keyword to match the text length</li>
                    <li>For each letter, shift it by the corresponding keyword letter's position in the alphabet</li>
                    <li>A=0, B=1, C=2, ..., Z=25</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs`}>
                    <div>Text:    HELLO WORLD</div>
                    <div>Keyword: SECRE TSECR</div>
                    <div>Result:  ZINCS OOPLJ</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Keyword Strength</h3>
                  <ul className="space-y-1">
                    <li><span className="text-red-500">●</span> <strong>Weak:</strong> Less than 3 characters</li>
                    <li><span className="text-yellow-500">●</span> <strong>Medium:</strong> 3-5 characters</li>
                    <li><span className="text-green-500">●</span> <strong>Strong:</strong> 6+ characters</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Context</h3>
                  <p>Described by Giovan Battista Bellaso in 1553 and later misattributed to Blaise de Vigenère. It was considered unbreakable for 300 years until Friedrich Kasiski published a method of deciphering it in 1863.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Numbers</label>
                  <input
                    type="checkbox"
                    checked={includeNumbers}
                    onChange={(e) => setIncludeNumbers(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Special Characters</label>
                  <input
                    type="checkbox"
                    checked={includeSpecialChars}
                    onChange={(e) => setIncludeSpecialChars(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Auto-repeat Keyword</label>
                  <input
                    type="checkbox"
                    checked={autoRepeatKey}
                    onChange={(e) => setAutoRepeatKey(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Remove Spaces</label>
                  <input
                    type="checkbox"
                    checked={removeSpaces}
                    onChange={(e) => setRemoveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Quick Keywords</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['CIPHER', 'CRYPTO', 'SECURE', 'HIDDEN', 'ENIGMA', 'STEALTH'].map(key => (
                      <button
                        key={key}
                        onClick={() => setKeyword(key)}
                        className={`p-2 text-xs rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.averageKeywordLength}</div>
                  <div className="text-sm">Avg Keyword Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeywords}</div>
                  <div className="text-sm">Unique Keywords</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-xl font-bold text-cyan-500">{stats.mostUsedKeyword}</div>
                  <div className="text-sm">Most Used Keyword</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${getStrengthColor(item.strength)} bg-opacity-20`}>
                            {item.strength.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          Key: {item.keyword}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate">{item.input}</div>
                        <div className="truncate opacity-60">→ {item.output}</div>
                      </div>
                      <div className="text-xs opacity-60 mt-1">
                        {item.timestamp.toLocaleString()}
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Lock size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default VigenereCipherApp;